<template>
  <div>
    <d-card-row>
      <h3 class="title is-6">
        {{ title }}
      </h3>
      <slot name="header" />
    </d-card-row>
    <d-card-row>
      <div class="columns">
        <div class="column is-three-quarters">
          <div class="field required">
            <label class="label">{{
              $t("attributes.address_1").capitalize()
            }}</label>
            <div class="control is-clearfix">
              <input
                ref="address1Field"
                :value="form.address_1"
                :name="prefix + 'address_1'"
                class="input"
                :class="{ 'is-danger': address1Error }"
                @keydown.enter.prevent
                @input="onInput('address_1', $event.target.value)"
              >

              <p
                v-if="address1Error"
                class="help is-danger"
              >
                {{ address1Error }}
              </p>
            </div>
          </div>
        </div>
        <div class="column">
          <v-input
            :value="form.address_2"
            :name="prefix + 'address_2'"
            @input="onInput('address_2', $event)"
          />
        </div>
      </div>
    </d-card-row>

    <d-card-row>
      <div class="columns">
        <div class="column is-half">
          <v-input
            :value="form.city"
            required
            :name="prefix + 'city'"
            @input="onInput('city', $event)"
          />
        </div>
        <div class="column is-half">
          <v-input
            :value="form.postal_code"
            required
            :name="prefix + 'postal_code'"
            @input="onInput('postal_code', $event)"
          />
        </div>
      </div>
    </d-card-row>

    <d-card-row>
      <div class="columns">
        <div class="column is-half">
          <v-input-autocomplete
            :name="prefix + 'country'"
            :placeholder="$t('country').capitalize()"
            open-on-focus
            required
            :data="$laravel.countries"
            :value="getCountryNameFromIsoCode(form.country)"
            field="name"
            @input="onAutoCompleteChange($event, 'country')"
          />
        </div>
        <div class="column is-half">
          <v-input-select
            :disabled="region_disabled"
            :value="form.province"
            required
            :name="prefix + 'province'"
            @input="onInput('province', $event)"
          >
            <option
              v-for="region in regions"
              :key="region.id"
              :value="formatProvinceValue(region.code)"
            >
              {{ region.name }}
            </option>
          </v-input-select>
        </div>
      </div>
    </d-card-row>
  </div>
</template>

<script>
export default {
  props: {
    title: {
      type: String,
      required: true,
    },
    value: {
      type: [Object, null],
      required: false,
      default: null,
    },
    prefix: {
      type: String,
      required: true,
    },
    errors: {
      default() {
        return {};
      },
      type: Object,
    },
  },
  data() {
    return {
      region_disabled: true,
      autocomplete: null,
      form: {},
    };
  },
  computed: {
    valueInternal() {
      if (!_.isObject(this.value)) {
        return {};
      }
      return this.value;
    },
    regions() {
      return this.$laravel.regions.filter((region) => {
        return region.country_id == this.valueInternal.country;
      });
    },
    autocompleteInput() {
      return this.$refs.address1Field;
    },
    address1Error() {
      const error = _.get(this.errors, this.prefix + "address_1", null);
      return error && error[0] ? error[0] : false;
    },
  },
  created() {
    this.onInput("country", this.valueInternal.country);
    this.form = _.cloneDeep(this.valueInternal);
  },
  mounted() {
    this.autocomplete = new google.maps.places.Autocomplete(
      this.autocompleteInput,
      {
        fields: ["address_components"],
        types: ["address"],
        componentRestrictions: { country: "ca" },
      }
    );

    this.autocomplete.addListener("place_changed", this.fillAddress);
  },
  methods: {
    fillAddress() {
      if (!this.autocomplete) {
        return;
      }
      // Get the place details from the autocomplete object.
      const place = this.autocomplete.getPlace();

      let address1 = "";

      const newForm = _.cloneDeep(this.valueInternal);
      this.$emit("input", newForm);
      if (!place.address_components) {
        return;
      }
      for (const component of place.address_components) {
        const componentType = component.types[0];
        switch (componentType) {
        case "street_number": {
          address1 = `${component.long_name} ${address1}`;
          newForm.address_1 = address1;
          break;
        }
        case "route": {
          address1 += component.long_name;
          newForm.address_1 = address1;
          break;
        }
        case "postal_code": {
          newForm.postal_code = `${component.long_name}`;
          break;
        }
        case "postal_code_prefix": {
          newForm.postal_code = `${component.long_name}`;
          break;
        }
        case "locality":
          newForm.city = component.long_name;
          break;
        case "administrative_area_level_1": {
          newForm.province = component.short_name.toLowerCase();
          break;
        }
        case "country":
          newForm.country = component.short_name.toLowerCase();
          break;
        }
      }
      this.$nextTick(() => {
        // Force value change
        this.form = newForm;
        this.$emit("input", newForm);
      });
    },
    formatProvinceValue(value) {
      let new_value = "";
      if (
        this.valueInternal.country !== undefined ||
        this.valueInternal.country.length > 0
      ) {
        new_value = value.split(this.valueInternal.country + "-")[1];
      }
      return new_value;
    },
    onInput(name, value) {
      const newAddress = _.cloneDeep(this.valueInternal);
      newAddress[name] = value;
      this.form[name] = value;
      this.$emit("input", newAddress);
    },
    onAutoCompleteChange(value, key) {
      if (value && value.code_2) {
        this.onInput(key, value.code_2);
        this.region_disabled = false;
      } else {
        this.onInput("province", null);
        this.onInput("country", null);
        this.region_disabled = true;
      }
    },
    getCountryNameFromIsoCode(code_2) {
      let countryObject = this.$laravel.countries.filter((country) => {
        return country.id === code_2;
      });
      if (countryObject.length > 0) {
        this.region_disabled = false;
        return countryObject[0].name;
      }
      return "";
    },
  },
};
</script>
